import { readAppEnvFresh } from '@/lib/env'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WsStatus = 'connecting' | 'connected' | 'disconnected'

type MessageListener = (msg: unknown) => void
type StatusListener = (status: WsStatus) => void

interface WsEntry {
  ws: WebSocket | null
  status: WsStatus
  reconnectDelay: number
  reconnectTimer: ReturnType<typeof setTimeout> | null
  pingTimer: ReturnType<typeof setInterval> | null
  dead: boolean
  listeners: Set<MessageListener>
  statusListeners: Set<StatusListener>
}

// ---------------------------------------------------------------------------
// Singleton registry: one WS per companyId, shared across all subscribers.
// Opened on first subscriber, closed when the last subscriber unsubscribes.
// ---------------------------------------------------------------------------

const registry = new Map<string, WsEntry>()

const STORAGE_KEY = 'vectraclip-jwt'
const PING_INTERVAL_MS = 25_000
const MAX_RECONNECT_DELAY_MS = 30_000

function readToken(): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (
      parsed &&
      typeof parsed === 'object' &&
      'accessToken' in parsed &&
      typeof (parsed as { accessToken: unknown }).accessToken === 'string'
    )
      return (parsed as { accessToken: string }).accessToken
    return raw
  } catch {
    return null
  }
}

function buildWsUrl(companyId: string): string | null {
  try {
    const env = readAppEnvFresh()
    let base = env.wsUrl
    if (!base && env.apiBaseUrl) {
      base = env.apiBaseUrl
        .replace(/^https:\/\//, 'wss://')
        .replace(/^http:\/\//, 'ws://')
        .replace(/\/api\/?$/, '')
        .replace(/\/$/, '') + '/ws'
    }
    if (!base) return null
    const token = readToken()
    const qs = token ? `?token=${encodeURIComponent(token)}` : ''
    return `${base}/companies/${companyId}${qs}`
  } catch {
    return null
  }
}

function setStatus(entry: WsEntry, status: WsStatus) {
  if (entry.status === status) return
  entry.status = status
  for (const fn of entry.statusListeners) fn(status)
}

function clearPing(entry: WsEntry) {
  if (entry.pingTimer) {
    clearInterval(entry.pingTimer)
    entry.pingTimer = null
  }
}

function openSocket(companyId: string, entry: WsEntry) {
  const url = buildWsUrl(companyId)
  if (!url) {
    setStatus(entry, 'disconnected')
    return
  }

  setStatus(entry, 'connecting')

  let ws: WebSocket
  try {
    ws = new WebSocket(url)
  } catch {
    setStatus(entry, 'disconnected')
    return
  }
  entry.ws = ws

  ws.onopen = () => {
    entry.reconnectDelay = 1_000
    setStatus(entry, 'connected')
    // Client-side ping every 25 s detects silently broken connections.
    clearPing(entry)
    entry.pingTimer = setInterval(() => {
      if (entry.ws?.readyState === WebSocket.OPEN) {
        try {
          entry.ws.send(JSON.stringify({ type: 'ping' }))
        } catch {
          entry.ws.close()
        }
      }
    }, PING_INTERVAL_MS)
  }

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data as string) as unknown
      // Pong responses are server acknowledgements only; don't broadcast.
      if (
        msg &&
        typeof msg === 'object' &&
        'type' in msg &&
        (msg as { type: unknown }).type === 'pong'
      )
        return
      for (const fn of entry.listeners) fn(msg)
    } catch {
      // Malformed frame — ignore.
    }
  }

  ws.onclose = () => {
    clearPing(entry)
    entry.ws = null
    setStatus(entry, 'disconnected')
    if (entry.dead || entry.listeners.size === 0) return
    entry.reconnectTimer = setTimeout(() => {
      if (!entry.dead && entry.listeners.size > 0) openSocket(companyId, entry)
      entry.reconnectDelay = Math.min(
        entry.reconnectDelay * 2,
        MAX_RECONNECT_DELAY_MS,
      )
    }, entry.reconnectDelay)
  }

  ws.onerror = () => ws.close()
}

function teardown(companyId: string, entry: WsEntry) {
  entry.dead = true
  clearPing(entry)
  if (entry.reconnectTimer) {
    clearTimeout(entry.reconnectTimer)
    entry.reconnectTimer = null
  }
  entry.ws?.close()
  registry.delete(companyId)
}

function getOrCreate(companyId: string): WsEntry {
  let entry = registry.get(companyId)
  if (!entry) {
    entry = {
      ws: null,
      status: 'disconnected',
      reconnectDelay: 1_000,
      reconnectTimer: null,
      pingTimer: null,
      dead: false,
      listeners: new Set(),
      statusListeners: new Set(),
    }
    registry.set(companyId, entry)
  }
  return entry
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const wsClient = {
  /**
   * Subscribe to raw WebSocket messages for a company.
   * Opens the socket on first subscriber; closes on last unsubscribe.
   * Returns an `unsubscribe` function.
   */
  subscribe(companyId: string, listener: MessageListener): () => void {
    const entry = getOrCreate(companyId)
    entry.listeners.add(listener)
    // Defer openSocket by one microtask so React StrictMode's synchronous
    // mount→cleanup→remount cycle can cancel before the WebSocket is created,
    // avoiding the "WebSocket closed before connection established" warning.
    if (!entry.ws && !entry.dead) {
      queueMicrotask(() => {
        const e = registry.get(companyId)
        if (e && !e.ws && !e.dead && e.listeners.size > 0) {
          openSocket(companyId, e)
        }
      })
    }
    return () => {
      const e = registry.get(companyId)
      if (!e) return
      e.listeners.delete(listener)
      if (e.listeners.size === 0 && e.statusListeners.size === 0) teardown(companyId, e)
    }
  },

  /**
   * Subscribe to connection status changes for a company.
   * Returns the current status synchronously and an `unsubscribe` callback.
   */
  subscribeStatus(
    companyId: string,
    listener: StatusListener,
  ): { unsubscribe: () => void; currentStatus: WsStatus } {
    const entry = getOrCreate(companyId)
    entry.statusListeners.add(listener)
    const currentStatus = entry.status
    // Keep WS alive even on routes that only observe status.
    if (!entry.ws && !entry.dead) {
      queueMicrotask(() => {
        const e = registry.get(companyId)
        if (e && !e.ws && !e.dead && (e.listeners.size > 0 || e.statusListeners.size > 0)) {
          openSocket(companyId, e)
        }
      })
    }
    return {
      currentStatus,
      unsubscribe: () => {
        const e = registry.get(companyId)
        if (!e) return
        e.statusListeners.delete(listener)
        if (e.listeners.size === 0 && e.statusListeners.size === 0) teardown(companyId, e)
      },
    }
  },

  /** Current connection status for a company (non-reactive snapshot). */
  getStatus(companyId: string): WsStatus {
    return registry.get(companyId)?.status ?? 'disconnected'
  },

  /** Tear down ALL company connections. Call on logout. */
  disconnectAll(): void {
    for (const [id, entry] of registry) {
      teardown(id, entry)
    }
  },
}
